import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Eye,
  Loader2,
  Beaker,
  FileText,
  Table2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Edit2,
  Copy,
  Sparkles,
  Send,
  MessageSquare,
  Settings,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

// Constants
const INDUSTRIES = [
  'Financial Services', 'Healthcare', 'Technology', 'Manufacturing',
  'Retail', 'Government', 'Education', 'Legal', 'Energy',
  'Telecommunications', 'General Business'
];

const TOPICS = [
  'Sanctions & AML Compliance', 'Anti-Corruption & Bribery', 'Data Privacy & Protection',
  'Conflict of Interest', 'Cybersecurity', 'Workplace Ethics', 'Anti-Money Laundering',
  'Health & Safety', 'Harassment Prevention', 'Information Security',
  'Regulatory Compliance', 'Risk Management', 'Corporate Governance'
];

const LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' }
];

const STEP_TYPES = [
  { value: 'case_study', label: 'Case Study', icon: FileText, description: 'Customer/case details display' },
  { value: 'information', label: 'Information', icon: Info, description: 'Display context without interaction' },
  { value: 'table', label: 'Comparison Table', icon: Table2, description: 'Data table display' },
  { value: 'choice', label: 'Decision Point', icon: CheckCircle, description: 'Multiple choice question' },
  { value: 'learning_point', label: 'Learning Point', icon: Sparkles, description: 'Key takeaway' }
];

const ALERT_LEVELS = [
  { value: 'critical', label: 'Critical (Red)', color: 'bg-red-500/20 border-red-500 text-red-400' },
  { value: 'warning', label: 'Warning (Orange)', color: 'bg-orange-500/20 border-orange-500 text-orange-400' },
  { value: 'success', label: 'Success (Green)', color: 'bg-green-500/20 border-green-500 text-green-400' },
  { value: 'info', label: 'Info (Blue)', color: 'bg-blue-500/20 border-blue-500 text-blue-400' }
];

const CONTENT_BLOCK_TYPES = [
  { value: 'text', label: 'Text', icon: FileText },
  { value: 'alert', label: 'Alert Box', icon: AlertTriangle },
  { value: 'table', label: 'Table', icon: Table2 },
  { value: 'list', label: 'Bullet List', icon: MessageSquare }
];

// Helper to generate unique IDs
const generateId = (prefix) => `${prefix}_${Math.random().toString(36).substr(2, 8)}`;

// Empty templates
const createEmptyChoice = (label) => ({
  choice_id: generateId('choice'),
  label,
  text: '',
  is_correct: false,
  feedback: '',
  points: 0
});

const createEmptyQuestion = () => ({
  question_id: generateId('q'),
  question_text: '',
  question_type: 'single',
  choices: [createEmptyChoice('A'), createEmptyChoice('B'), createEmptyChoice('C'), createEmptyChoice('D')],
  explanation: ''
});

const createEmptyContentBlock = (type = 'text') => ({
  block_id: generateId('block'),
  block_type: type,
  content: '',
  alert_level: type === 'alert' ? 'warning' : null,
  alert_title: type === 'alert' ? '' : null,
  table_headers: type === 'table' ? ['Column 1', 'Column 2', 'Column 3'] : null,
  table_rows: type === 'table' ? [['', '', '']] : null,
  list_items: type === 'list' ? [''] : null
});

const createEmptyStep = (order = 0) => ({
  step_id: generateId('step'),
  step_type: 'information',
  title: 'New Step',
  order,
  content_blocks: [createEmptyContentBlock('text')],
  questions: [],
  is_entry_point: order === 0,
  learning_points: []
});

const createEmptyScenario = (order = 0) => ({
  scenario_id: generateId('scenario'),
  title: `Scenario ${order + 1}`,
  description: '',
  order,
  case_details: null,
  steps: [createEmptyStep(0)],
  learning_objectives: [],
  key_learning_point: ''
});

// Sub-components
const ContentBlockEditor = ({ block, onChange, onDelete }) => {
  const updateBlock = (field, value) => {
    onChange({ ...block, [field]: value });
  };

  const alertConfig = ALERT_LEVELS.find(a => a.value === block.alert_level);

  return (
    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-600">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Select value={block.block_type} onValueChange={(v) => updateBlock('block_type', v)}>
            <SelectTrigger className="w-36 bg-slate-700 border-slate-600">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CONTENT_BLOCK_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>
                  <span className="flex items-center gap-2">
                    <t.icon className="w-4 h-4" />
                    {t.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-400 hover:text-red-300">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {block.block_type === 'text' && (
        <Textarea
          value={block.content}
          onChange={(e) => updateBlock('content', e.target.value)}
          placeholder="Enter text content..."
          className="bg-slate-700 border-slate-600 min-h-24"
        />
      )}

      {block.block_type === 'alert' && (
        <div className="space-y-3">
          <div className="flex gap-3">
            <Select value={block.alert_level || 'warning'} onValueChange={(v) => updateBlock('alert_level', v)}>
              <SelectTrigger className="w-40 bg-slate-700 border-slate-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALERT_LEVELS.map(a => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={block.alert_title || ''}
              onChange={(e) => updateBlock('alert_title', e.target.value)}
              placeholder="Alert title (e.g., SCREENING ALERT)"
              className="flex-1 bg-slate-700 border-slate-600"
            />
          </div>
          <div className={`p-3 rounded border ${alertConfig?.color || 'bg-slate-700'}`}>
            <Textarea
              value={block.content}
              onChange={(e) => updateBlock('content', e.target.value)}
              placeholder="Alert message content..."
              className="bg-transparent border-none min-h-16 resize-none"
            />
          </div>
        </div>
      )}

      {block.block_type === 'table' && (
        <div className="space-y-3">
          <div className="flex gap-2 items-center">
            <span className="text-sm text-slate-400">Headers:</span>
            {(block.table_headers || []).map((header, i) => (
              <Input
                key={i}
                value={header}
                onChange={(e) => {
                  const newHeaders = [...(block.table_headers || [])];
                  newHeaders[i] = e.target.value;
                  updateBlock('table_headers', newHeaders);
                }}
                className="w-32 bg-slate-700 border-slate-600"
              />
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateBlock('table_headers', [...(block.table_headers || []), 'New Column'])}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-2">
            {(block.table_rows || []).map((row, rowIdx) => (
              <div key={rowIdx} className="flex gap-2 items-center">
                {row.map((cell, cellIdx) => (
                  <Input
                    key={cellIdx}
                    value={cell}
                    onChange={(e) => {
                      const newRows = [...(block.table_rows || [])];
                      newRows[rowIdx][cellIdx] = e.target.value;
                      updateBlock('table_rows', newRows);
                    }}
                    className="w-32 bg-slate-700 border-slate-600"
                  />
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const newRows = (block.table_rows || []).filter((_, i) => i !== rowIdx);
                    updateBlock('table_rows', newRows);
                  }}
                  className="text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const colCount = (block.table_headers || []).length || 3;
                updateBlock('table_rows', [...(block.table_rows || []), Array(colCount).fill('')]);
              }}
            >
              <Plus className="w-4 h-4 mr-1" /> Add Row
            </Button>
          </div>
        </div>
      )}

      {block.block_type === 'list' && (
        <div className="space-y-2">
          {(block.list_items || []).map((item, i) => (
            <div key={i} className="flex gap-2 items-center">
              <span className="text-slate-400">•</span>
              <Input
                value={item}
                onChange={(e) => {
                  const newItems = [...(block.list_items || [])];
                  newItems[i] = e.target.value;
                  updateBlock('list_items', newItems);
                }}
                className="flex-1 bg-slate-700 border-slate-600"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const newItems = (block.list_items || []).filter((_, idx) => idx !== i);
                  updateBlock('list_items', newItems);
                }}
                className="text-red-400"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => updateBlock('list_items', [...(block.list_items || []), ''])}
          >
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        </div>
      )}
    </div>
  );
};

const QuestionEditor = ({ question, onChange, onDelete }) => {
  const updateQuestion = (field, value) => {
    onChange({ ...question, [field]: value });
  };

  const updateChoice = (index, field, value) => {
    const newChoices = [...question.choices];
    newChoices[index] = { ...newChoices[index], [field]: value };
    
    // If marking as correct, ensure points are set
    if (field === 'is_correct' && value) {
      newChoices[index].points = 10;
    }
    
    onChange({ ...question, choices: newChoices });
  };

  return (
    <div className="bg-slate-800/50 rounded-lg p-4 border border-amber-500/30">
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-amber-400 font-medium flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          Question
        </h5>
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-400 hover:text-red-300">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <Textarea
        value={question.question_text}
        onChange={(e) => updateQuestion('question_text', e.target.value)}
        placeholder="What should the analyst do?"
        className="bg-slate-700 border-slate-600 mb-4"
      />

      <div className="space-y-3">
        <h6 className="text-sm text-slate-400">Answer Choices</h6>
        {question.choices.map((choice, i) => (
          <div key={choice.choice_id} className={`p-3 rounded-lg border ${choice.is_correct ? 'border-green-500 bg-green-500/10' : 'border-slate-600 bg-slate-700/50'}`}>
            <div className="flex items-start gap-3">
              <span className={`font-bold ${choice.is_correct ? 'text-green-400' : 'text-slate-400'}`}>
                {choice.label})
              </span>
              <div className="flex-1 space-y-2">
                <Input
                  value={choice.text}
                  onChange={(e) => updateChoice(i, 'text', e.target.value)}
                  placeholder="Answer choice text..."
                  className="bg-slate-800 border-slate-600"
                />
                <Input
                  value={choice.feedback || ''}
                  onChange={(e) => updateChoice(i, 'feedback', e.target.value)}
                  placeholder="Feedback if selected..."
                  className="bg-slate-800 border-slate-600 text-sm"
                />
              </div>
              <button
                onClick={() => updateChoice(i, 'is_correct', !choice.is_correct)}
                className={`p-2 rounded ${choice.is_correct ? 'bg-green-500 text-white' : 'bg-slate-600 text-slate-300'}`}
                title={choice.is_correct ? 'Correct Answer' : 'Mark as Correct'}
              >
                {choice.is_correct ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4">
        <label className="text-sm text-slate-400 block mb-1">Explanation (shown after answering)</label>
        <Textarea
          value={question.explanation || ''}
          onChange={(e) => updateQuestion('explanation', e.target.value)}
          placeholder="Explain why the correct answer is correct..."
          className="bg-slate-700 border-slate-600"
        />
      </div>
    </div>
  );
};

const StepEditor = ({ step, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(true);

  const updateStep = (field, value) => {
    onUpdate({ ...step, [field]: value });
  };

  const addContentBlock = (type = 'text') => {
    updateStep('content_blocks', [...step.content_blocks, createEmptyContentBlock(type)]);
  };

  const updateContentBlock = (index, block) => {
    const newBlocks = [...step.content_blocks];
    newBlocks[index] = block;
    updateStep('content_blocks', newBlocks);
  };

  const deleteContentBlock = (index) => {
    updateStep('content_blocks', step.content_blocks.filter((_, i) => i !== index));
  };

  const addQuestion = () => {
    updateStep('questions', [...step.questions, createEmptyQuestion()]);
  };

  const updateQuestion = (index, question) => {
    const newQuestions = [...step.questions];
    newQuestions[index] = question;
    updateStep('questions', newQuestions);
  };

  const deleteQuestion = (index) => {
    updateStep('questions', step.questions.filter((_, i) => i !== index));
  };

  const stepTypeConfig = STEP_TYPES.find(t => t.value === step.step_type);
  const StepIcon = stepTypeConfig?.icon || Info;

  return (
    <div className="bg-slate-800 rounded-lg border border-slate-600 overflow-hidden">
      <div
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-700/50"
        onClick={() => setExpanded(!expanded)}
      >
        <GripVertical className="w-4 h-4 text-slate-500" />
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <StepIcon className="w-5 h-5 text-amber-400" />
        <span className="flex-1 font-medium">{step.title || 'Untitled Step'}</span>
        <span className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-400">{stepTypeConfig?.label}</span>
        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-red-400">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {expanded && (
        <div className="p-4 pt-0 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Step Title</label>
              <Input
                value={step.title}
                onChange={(e) => updateStep('title', e.target.value)}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Step Type</label>
              <Select value={step.step_type} onValueChange={(v) => updateStep('step_type', v)}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STEP_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="flex items-center gap-2">
                        <t.icon className="w-4 h-4" />
                        {t.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Content Blocks */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h5 className="text-sm font-medium text-slate-300">Content Blocks</h5>
              <div className="flex gap-2">
                {CONTENT_BLOCK_TYPES.map(t => (
                  <Button
                    key={t.value}
                    variant="ghost"
                    size="sm"
                    onClick={() => addContentBlock(t.value)}
                    className="text-slate-400 hover:text-white"
                  >
                    <t.icon className="w-4 h-4 mr-1" />
                    {t.label}
                  </Button>
                ))}
              </div>
            </div>
            {step.content_blocks.map((block, i) => (
              <ContentBlockEditor
                key={block.block_id}
                block={block}
                onChange={(b) => updateContentBlock(i, b)}
                onDelete={() => deleteContentBlock(i)}
              />
            ))}
          </div>

          {/* Questions */}
          {['choice', 'assessment', 'multi_choice'].includes(step.step_type) && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h5 className="text-sm font-medium text-amber-400">Questions</h5>
                <Button variant="ghost" size="sm" onClick={addQuestion} className="text-amber-400">
                  <Plus className="w-4 h-4 mr-1" /> Add Question
                </Button>
              </div>
              {step.questions.map((q, i) => (
                <QuestionEditor
                  key={q.question_id}
                  question={q}
                  onChange={(updated) => updateQuestion(i, updated)}
                  onDelete={() => deleteQuestion(i)}
                />
              ))}
              {step.questions.length === 0 && (
                <p className="text-sm text-slate-500 italic">No questions yet. Add one above.</p>
              )}
            </div>
          )}

          {/* Learning Points */}
          <div>
            <label className="text-sm text-slate-400 block mb-1">Learning Points (key takeaways)</label>
            <div className="space-y-2">
              {(step.learning_points || []).map((point, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={point}
                    onChange={(e) => {
                      const newPoints = [...(step.learning_points || [])];
                      newPoints[i] = e.target.value;
                      updateStep('learning_points', newPoints);
                    }}
                    className="flex-1 bg-slate-700 border-slate-600"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateStep('learning_points', step.learning_points.filter((_, idx) => idx !== i))}
                    className="text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateStep('learning_points', [...(step.learning_points || []), ''])}
              >
                <Plus className="w-4 h-4 mr-1" /> Add Learning Point
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ScenarioEditor = ({ scenario, onUpdate, onDelete, scenarioIndex }) => {
  const [expanded, setExpanded] = useState(true);

  const updateScenario = (field, value) => {
    onUpdate({ ...scenario, [field]: value });
  };

  const addStep = () => {
    const newStep = createEmptyStep(scenario.steps.length);
    updateScenario('steps', [...scenario.steps, newStep]);
  };

  const updateStep = (index, step) => {
    const newSteps = [...scenario.steps];
    newSteps[index] = step;
    updateScenario('steps', newSteps);
  };

  const deleteStep = (index) => {
    updateScenario('steps', scenario.steps.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden">
      <div
        className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-500/10 to-transparent cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <GripVertical className="w-5 h-5 text-slate-500" />
        {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        <Beaker className="w-5 h-5 text-amber-400" />
        <span className="flex-1 font-semibold text-lg">{scenario.title || `Scenario ${scenarioIndex + 1}`}</span>
        <span className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-400">
          {scenario.steps.length} step{scenario.steps.length !== 1 ? 's' : ''}
        </span>
        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-red-400">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {expanded && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Scenario Title</label>
              <Input
                value={scenario.title}
                onChange={(e) => updateScenario('title', e.target.value)}
                className="bg-slate-800 border-slate-600"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Key Learning Point</label>
              <Input
                value={scenario.key_learning_point || ''}
                onChange={(e) => updateScenario('key_learning_point', e.target.value)}
                placeholder="Main takeaway from this scenario..."
                className="bg-slate-800 border-slate-600"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-400 block mb-1">Description</label>
            <Textarea
              value={scenario.description || ''}
              onChange={(e) => updateScenario('description', e.target.value)}
              placeholder="Brief description of this scenario..."
              className="bg-slate-800 border-slate-600"
            />
          </div>

          {/* Case Details */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-600">
            <h5 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Case/Customer Details (Optional)
            </h5>
            <div className="grid grid-cols-2 gap-3">
              <Input
                value={scenario.case_details?.name || ''}
                onChange={(e) => updateScenario('case_details', { ...(scenario.case_details || {}), name: e.target.value })}
                placeholder="Name"
                className="bg-slate-700 border-slate-600"
              />
              <Input
                value={scenario.case_details?.nationality || ''}
                onChange={(e) => updateScenario('case_details', { ...(scenario.case_details || {}), nationality: e.target.value })}
                placeholder="Nationality"
                className="bg-slate-700 border-slate-600"
              />
              <Input
                value={scenario.case_details?.dob || ''}
                onChange={(e) => updateScenario('case_details', { ...(scenario.case_details || {}), dob: e.target.value })}
                placeholder="Date of Birth"
                className="bg-slate-700 border-slate-600"
              />
              <Input
                value={scenario.case_details?.occupation || ''}
                onChange={(e) => updateScenario('case_details', { ...(scenario.case_details || {}), occupation: e.target.value })}
                placeholder="Occupation"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <Textarea
              value={scenario.case_details?.context || ''}
              onChange={(e) => updateScenario('case_details', { ...(scenario.case_details || {}), context: e.target.value })}
              placeholder="Additional context..."
              className="bg-slate-700 border-slate-600 mt-3"
            />
          </div>

          {/* Steps */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h5 className="font-medium text-slate-300">Steps</h5>
              <Button variant="outline" size="sm" onClick={addStep} className="border-amber-500/50 text-amber-400">
                <Plus className="w-4 h-4 mr-1" /> Add Step
              </Button>
            </div>
            {scenario.steps.map((step, i) => (
              <StepEditor
                key={step.step_id}
                step={step}
                onUpdate={(s) => updateStep(i, s)}
                onDelete={() => deleteStep(i)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
export default function LearningLabBuilderV2() {
  const navigate = useNavigate();
  const { labId } = useParams();
  const isEditMode = !!labId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [lab, setLab] = useState({
    title: '',
    description: '',
    learning_objective: '',
    industry: 'Financial Services',
    topic: 'Sanctions & AML Compliance',
    level: 'intermediate',
    thumbnail_url: '',
    estimated_duration_minutes: 30,
    cpd_points: 0,
    tags: [],
    scenarios: [createEmptyScenario(0)],
    final_assessment: [],
    mental_model: null
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Load existing lab
  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      axios.get(`${API}/learning-labs-v2/${labId}`, { headers })
        .then(res => {
          setLab(res.data);
        })
        .catch(err => {
          console.error('Failed to load lab:', err);
          toast.error('Failed to load learning lab');
        })
        .finally(() => setLoading(false));
    }
  }, [labId, isEditMode]);

  const updateLab = (field, value) => {
    setLab(prev => ({ ...prev, [field]: value }));
  };

  const addScenario = () => {
    updateLab('scenarios', [...lab.scenarios, createEmptyScenario(lab.scenarios.length)]);
  };

  const updateScenario = (index, scenario) => {
    const newScenarios = [...lab.scenarios];
    newScenarios[index] = scenario;
    updateLab('scenarios', newScenarios);
  };

  const deleteScenario = (index) => {
    if (lab.scenarios.length <= 1) {
      toast.error('Lab must have at least one scenario');
      return;
    }
    updateLab('scenarios', lab.scenarios.filter((_, i) => i !== index));
  };

  const saveLab = async () => {
    if (!lab.title.trim()) {
      toast.error('Please enter a lab title');
      return;
    }
    if (lab.scenarios.length === 0) {
      toast.error('Please add at least one scenario');
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await axios.put(`${API}/learning-labs-v2/${labId}`, lab, { headers });
        toast.success('Lab updated successfully');
      } else {
        const res = await axios.post(`${API}/learning-labs-v2`, lab, { headers });
        toast.success('Lab created successfully');
        navigate(`/admin/labs-v2/${res.data.lab_id}/edit`);
      }
    } catch (err) {
      console.error('Failed to save lab:', err);
      toast.error('Failed to save lab');
    } finally {
      setSaving(false);
    }
  };

  const publishLab = async () => {
    if (!isEditMode) {
      toast.error('Please save the lab first');
      return;
    }
    try {
      await axios.post(`${API}/learning-labs-v2/${labId}/publish`, {}, { headers });
      toast.success('Lab published successfully');
    } catch (err) {
      console.error('Failed to publish:', err);
      toast.error('Failed to publish lab');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur border-b border-slate-700 px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin')} className="text-slate-400">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {isEditMode ? 'Edit Learning Lab V2' : 'Create Learning Lab V2'}
                </h1>
                <p className="text-sm text-slate-400">Multi-scenario interactive learning experience</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isEditMode && (
                <Button variant="outline" onClick={() => navigate(`/labs-v2/${labId}`)} className="border-slate-600">
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
              )}
              <Button onClick={saveLab} disabled={saving} className="bg-amber-500 hover:bg-amber-600">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
              {isEditMode && (
                <Button onClick={publishLab} className="bg-green-600 hover:bg-green-700">
                  <Send className="w-4 h-4 mr-2" />
                  Publish
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
          {/* Lab Details */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-400" />
              Lab Details
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm text-slate-400 block mb-1">Title</label>
                <Input
                  value={lab.title}
                  onChange={(e) => updateLab('title', e.target.value)}
                  placeholder="Sanctions Screening — A Match Does Not Always Mean the Same Thing"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm text-slate-400 block mb-1">Description</label>
                <Textarea
                  value={lab.description}
                  onChange={(e) => updateLab('description', e.target.value)}
                  placeholder="Learn how to properly investigate and respond to sanctions screening alerts..."
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm text-slate-400 block mb-1">Learning Objective</label>
                <Textarea
                  value={lab.learning_objective || ''}
                  onChange={(e) => updateLab('learning_objective', e.target.value)}
                  placeholder="By the end of this lab, learners will understand..."
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Industry</label>
                <Select value={lab.industry} onValueChange={(v) => updateLab('industry', v)}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Topic</label>
                <Select value={lab.topic} onValueChange={(v) => updateLab('topic', v)}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TOPICS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Level</label>
                <Select value={lab.level} onValueChange={(v) => updateLab('level', v)}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Duration (minutes)</label>
                <Input
                  type="number"
                  value={lab.estimated_duration_minutes}
                  onChange={(e) => updateLab('estimated_duration_minutes', parseInt(e.target.value) || 30)}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">CPD Points</label>
                <Input
                  type="number"
                  step="0.5"
                  value={lab.cpd_points}
                  onChange={(e) => updateLab('cpd_points', parseFloat(e.target.value) || 0)}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Tags (comma separated)</label>
                <Input
                  value={(lab.tags || []).join(', ')}
                  onChange={(e) => updateLab('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                  placeholder="sanctions, AML, compliance"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
          </div>

          {/* Scenarios */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Beaker className="w-5 h-5 text-amber-400" />
                Scenarios
              </h2>
              <Button onClick={addScenario} className="bg-amber-500 hover:bg-amber-600">
                <Plus className="w-4 h-4 mr-2" />
                Add Scenario
              </Button>
            </div>
            
            {lab.scenarios.map((scenario, i) => (
              <ScenarioEditor
                key={scenario.scenario_id}
                scenario={scenario}
                scenarioIndex={i}
                onUpdate={(s) => updateScenario(i, s)}
                onDelete={() => deleteScenario(i)}
              />
            ))}
          </div>

          {/* Mental Model */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              Mental Model (Framework Summary)
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Framework Title</label>
                <Input
                  value={lab.mental_model?.title || ''}
                  onChange={(e) => updateLab('mental_model', { ...(lab.mental_model || {}), title: e.target.value, steps: lab.mental_model?.steps || [] })}
                  placeholder="SANCTIONS SCREENING: DON'T STOP AT 'MATCH'"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Key Message</label>
                <Textarea
                  value={lab.mental_model?.key_message || ''}
                  onChange={(e) => updateLab('mental_model', { ...(lab.mental_model || {}), key_message: e.target.value })}
                  placeholder="A sanctions screening alert tells you where to start investigating. It does not tell you what decision to make."
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-2">Framework Steps</label>
                <div className="space-y-2">
                  {(lab.mental_model?.steps || []).map((step, i) => (
                    <div key={i} className="flex gap-2 items-start">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-500/20 text-amber-400 font-bold text-sm">
                        {step.number || i + 1}
                      </span>
                      <div className="flex-1 space-y-1">
                        <Input
                          value={step.title || ''}
                          onChange={(e) => {
                            const newSteps = [...(lab.mental_model?.steps || [])];
                            newSteps[i] = { ...newSteps[i], title: e.target.value };
                            updateLab('mental_model', { ...(lab.mental_model || {}), steps: newSteps });
                          }}
                          placeholder="Step title"
                          className="bg-slate-700 border-slate-600"
                        />
                        <Input
                          value={step.description || ''}
                          onChange={(e) => {
                            const newSteps = [...(lab.mental_model?.steps || [])];
                            newSteps[i] = { ...newSteps[i], description: e.target.value };
                            updateLab('mental_model', { ...(lab.mental_model || {}), steps: newSteps });
                          }}
                          placeholder="Step description"
                          className="bg-slate-700 border-slate-600"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newSteps = (lab.mental_model?.steps || []).filter((_, idx) => idx !== i);
                          updateLab('mental_model', { ...(lab.mental_model || {}), steps: newSteps });
                        }}
                        className="text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const steps = lab.mental_model?.steps || [];
                      updateLab('mental_model', {
                        ...(lab.mental_model || {}),
                        steps: [...steps, { number: steps.length + 1, title: '', description: '' }]
                      });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Step
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
